/**
 * FlagshipPracticeV3 — Wave 2 Practice scenes.
 *   Reflection, Friction, Spring (Hooke), Enzyme, Stomata.
 * Follows the same shell/panel/feedback pattern as V2.
 */
import React, { useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Panel, Feedback, NextBar, Shell } from "./PracticeScaffold";

// (Panel/Feedback/NextBar/Shell now live in ./PracticeScaffold — was duplicated here until iter-26)

// ============================================================================
// REFLECTION — plane mirror, incident + reflected rays
// ============================================================================
export const ReflectionPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [i, setI] = useState(30);
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const step = steps[stepIdx];

  const check = () => {
    const id = step.id;
    const in_range = (t) => Math.abs(i - t) <= 5;
    if (id === "p1" && in_range(30)) { setStatus((s) => ({ ...s, p1: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
    else if (id === "p1") setFb({ type: "warn", msg: step.mistakes?.[0]?.hint || "Aim near 30°." });
    else if (id === "p2" && in_range(45)) { setStatus((s) => ({ ...s, p2: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
    else if (id === "p2") setFb({ type: "warn", msg: "Aim near 45°." });
    else if (id === "p3" && in_range(60)) { setStatus((s) => ({ ...s, p3: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
    else if (id === "p3") setFb({ type: "warn", msg: "Aim near 60°." });
    else if (id === "p4") { setStatus((s) => ({ ...s, p4: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
  };
  const reset = () => { setI(30); setStatus({}); setFb(null); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  const rad = i * Math.PI / 180;
  const cx = 250, cy = 400;
  const rayLen = 200;
  const inX = cx - rayLen * Math.sin(rad);
  const inY = cy - rayLen * Math.cos(rad);
  const outX = cx + rayLen * Math.sin(rad);
  const outY = cy - rayLen * Math.cos(rad);

  const scene = (
    <svg viewBox="0 0 500 500" className="w-full h-full" data-testid="practice-canvas">
      {/* Mirror */}
      <rect x="60" y="400" width="380" height="12" fill="url(#mirGrad)" stroke="#0891b2" strokeWidth="2" />
      <defs>
        <linearGradient id="mirGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#94a3b8" /><stop offset="100%" stopColor="#475569" />
        </linearGradient>
        <marker id="arrRefl" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0, 8 4, 0 8" fill="#22d3ee" /></marker>
      </defs>
      {/* Hatched below mirror */}
      {[...Array(10)].map((_, k) => (
        <line key={k} x1={60 + k * 38} y1="412" x2={60 + k * 38 + 20} y2="440" stroke="#334155" strokeWidth="2" />
      ))}
      {/* Normal (vertical dashed) */}
      <line x1={cx} y1={cy} x2={cx} y2={cy - 280} stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="6 4" />
      <text x={cx + 6} y={cy - 275} fill="#f59e0b" fontSize="10" fontFamily="monospace">Normal</text>
      {/* Incident ray */}
      <line x1={inX} y1={inY} x2={cx} y2={cy} stroke="#fde047" strokeWidth="3" />
      <line x1={inX} y1={inY} x2={inX + 20 * Math.sin(rad)} y2={inY + 20 * Math.cos(rad)} stroke="#fde047" strokeWidth="0" />
      {/* Reflected ray */}
      <line x1={cx} y1={cy} x2={outX} y2={outY} stroke="#22d3ee" strokeWidth="3" markerEnd="url(#arrRefl)" />
      {/* Angle arcs */}
      <path d={`M ${cx - 26} ${cy - 44} A 50 50 0 0 1 ${cx - 46} ${cy - 20}`} fill="none" stroke="#fde047" strokeWidth="1.5" />
      <text x={cx - 60} y={cy - 32} fill="#fde047" fontSize="12" fontFamily="monospace">{`i=${i}°`}</text>
      <path d={`M ${cx + 46} ${cy - 20} A 50 50 0 0 1 ${cx + 26} ${cy - 44}`} fill="none" stroke="#22d3ee" strokeWidth="1.5" />
      <text x={cx + 32} y={cy - 32} fill="#22d3ee" fontSize="12" fontFamily="monospace">{`r=${i}°`}</text>
      <text x="250" y="470" fill="#94a3b8" fontSize="11" textAnchor="middle" fontFamily="monospace">|i − r| = 0° · law verified live</text>
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4">
        <div className="text-xs text-slate-500 mb-2 font-mono">Angle of incidence i</div>
        <Slider min={10} max={80} step={1} value={[i]} onValueChange={(v) => setI(v[0])} data-testid="reflection-incident" />
        <div className="mt-3 flex justify-between text-xs text-slate-500 font-mono"><span>10°</span><span className="text-cyan-300">i = {i}°</span><span>80°</span></div>
        <Button size="sm" className="mt-3 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={check} data-testid="reflection-check">Check this step</Button>
      </div>
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};

// ============================================================================
// FRICTION — block + pulley + effort
// ============================================================================
export const FrictionPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [N, setN] = useState(2);        // Newtons
  const [effort, setEffort] = useState(0); // Newtons
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const [readings, setReadings] = useState({});
  const step = steps[stepIdx];

  const MU = 0.4;
  const limit = MU * N;                 // F needed to start sliding
  const isSliding = effort >= limit;

  const check = () => {
    const id = step.id;
    const N_ok = (t) => Math.abs(N - t) <= 0.5;
    const F_at_limit = Math.abs(effort - limit) <= 0.15;
    if (id === "p1" && N_ok(2) && F_at_limit) { setStatus((s) => ({ ...s, p1: "ok" })); setReadings((r) => ({ ...r, F2: effort })); setFb({ type: "ok", msg: `${step.correct_hint} You found F = ${effort.toFixed(2)} N.` }); }
    else if (id === "p1") setFb({ type: "warn", msg: !N_ok(2) ? "Set the block load to 2 N first." : "Adjust the effort until the block just starts sliding." });
    else if (id === "p2" && N_ok(4) && F_at_limit) { setStatus((s) => ({ ...s, p2: "ok" })); setReadings((r) => ({ ...r, F4: effort })); setFb({ type: "ok", msg: `${step.correct_hint} F = ${effort.toFixed(2)} N.` }); }
    else if (id === "p2") setFb({ type: "warn", msg: !N_ok(4) ? "Set N = 4 N first." : "Nudge effort to the sliding threshold." });
    else if (id === "p3" && N_ok(6) && F_at_limit) { setStatus((s) => ({ ...s, p3: "ok" })); setReadings((r) => ({ ...r, F6: effort })); setFb({ type: "ok", msg: `${step.correct_hint} F = ${effort.toFixed(2)} N.` }); }
    else if (id === "p3") setFb({ type: "warn", msg: !N_ok(6) ? "Set N = 6 N first." : "Nudge effort to the sliding threshold." });
    else if (id === "p4") { setStatus((s) => ({ ...s, p4: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
  };
  const reset = () => { setN(2); setEffort(0); setStatus({}); setFb(null); setReadings({}); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  // block visuals: slides right when isSliding
  const blockOffset = isSliding ? 40 : 0;
  const scene = (
    <svg viewBox="0 0 600 500" className="w-full h-full" data-testid="practice-canvas">
      {/* Table */}
      <rect x="20" y="300" width="470" height="8" fill="#334155" />
      {/* Pulley */}
      <circle cx="490" cy="300" r="14" fill="#94a3b8" stroke="#475569" strokeWidth="2" />
      <circle cx="490" cy="300" r="4" fill="#0f172a" />
      {/* Block */}
      <g transform={`translate(${100 + blockOffset}, 0)`}>
        <rect x="0" y="260" width="80" height="40" fill="#f97316" stroke="#c2410c" strokeWidth="2" rx="3" />
        <text x="40" y="285" fill="#0f172a" fontSize="12" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{`${N} N`}</text>
        {/* Pull string */}
        <line x1="80" y1="280" x2="490" y2="300" stroke="#94a3b8" strokeWidth="2" />
      </g>
      {/* Hanging weight */}
      <g transform={`translate(490, ${310 + effort * 15})`}>
        <line x1="0" y1="-10" x2="0" y2="0" stroke="#94a3b8" strokeWidth="2" />
        <rect x="-18" y="0" width="36" height="40" fill="#eab308" stroke="#a16207" strokeWidth="2" rx="3" />
        <text x="0" y="24" fill="#0f172a" fontSize="11" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{`${effort.toFixed(1)} N`}</text>
      </g>
      {/* Status */}
      <g transform="translate(30, 40)">
        <rect x="0" y="0" width="170" height="50" fill="#0f172a" stroke={isSliding ? "#22c55e" : "#f59e0b"} strokeWidth="2" rx="6" />
        <text x="85" y="20" fill="#22d3ee" fontSize="10" textAnchor="middle" fontFamily="monospace">Threshold F = μN</text>
        <text x="85" y="40" fill={isSliding ? "#22c55e" : "#f59e0b"} fontSize="14" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{isSliding ? `SLIDING (F=${limit.toFixed(1)} N)` : "static"}</text>
      </g>
      {readings.F2 !== undefined && <text x="30" y="470" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`F₂ = ${readings.F2.toFixed(2)} N`}</text>}
      {readings.F4 !== undefined && <text x="180" y="470" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`F₄ = ${readings.F4.toFixed(2)} N`}</text>}
      {readings.F6 !== undefined && <text x="330" y="470" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`F₆ = ${readings.F6.toFixed(2)} N`}</text>}
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 space-y-4">
        <div>
          <div className="text-xs text-slate-500 mb-2 font-mono">Normal reaction N (block load)</div>
          <Slider min={1} max={8} step={0.5} value={[N]} onValueChange={(v) => setN(v[0])} data-testid="friction-normal" />
          <div className="mt-1 flex justify-between text-xs text-slate-500 font-mono"><span>1 N</span><span className="text-cyan-300">N = {N} N</span><span>8 N</span></div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-2 font-mono">Pulling effort F</div>
          <Slider min={0} max={4} step={0.05} value={[effort]} onValueChange={(v) => setEffort(v[0])} data-testid="friction-effort" />
          <div className="mt-1 flex justify-between text-xs text-slate-500 font-mono"><span>0 N</span><span className="text-cyan-300">F = {effort.toFixed(2)} N</span><span>4 N</span></div>
        </div>
        <Button size="sm" className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={check} data-testid="friction-check">Record this reading</Button>
      </div>
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};

// ============================================================================
// SPRING — Hooke's law
// ============================================================================
export const SpringPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [m, setM] = useState(50); // grams
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const [readings, setReadings] = useState({});
  const step = steps[stepIdx];

  const k = 50; // N/m
  const x = (m / 1000) * 9.8 / k * 100; // cm
  const check = () => {
    const id = step.id;
    const m_ok = (t) => Math.abs(m - t) <= 15;
    if (id === "p1" && m_ok(50)) { setStatus((s) => ({ ...s, p1: "ok" })); setReadings((r) => ({ ...r, x50: x })); setFb({ type: "ok", msg: `${step.correct_hint} x = ${x.toFixed(2)} cm.` }); }
    else if (id === "p1") setFb({ type: "warn", msg: step.mistakes?.[0]?.hint || "Aim near 50 g." });
    else if (id === "p2" && m_ok(100)) { setStatus((s) => ({ ...s, p2: "ok" })); setReadings((r) => ({ ...r, x100: x })); setFb({ type: "ok", msg: `${step.correct_hint} x = ${x.toFixed(2)} cm.` }); }
    else if (id === "p2") setFb({ type: "warn", msg: "Aim near 100 g." });
    else if (id === "p3" && m_ok(200)) { setStatus((s) => ({ ...s, p3: "ok" })); setReadings((r) => ({ ...r, x200: x })); setFb({ type: "ok", msg: `${step.correct_hint} x = ${x.toFixed(2)} cm.` }); }
    else if (id === "p3") setFb({ type: "warn", msg: "Aim near 200 g." });
    else if (id === "p4") { setStatus((s) => ({ ...s, p4: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
  };
  const reset = () => { setM(50); setStatus({}); setFb(null); setReadings({}); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  // Draw spring coils extending by extension
  const springTop = 70;
  const relaxedLen = 100;
  const stretched = relaxedLen + x * 10; // 10 px per cm
  const coilCount = 12;
  const coils = [...Array(coilCount)].map((_, k2) => {
    const yTop = springTop + (stretched * k2) / coilCount;
    const yBot = springTop + (stretched * (k2 + 1)) / coilCount;
    const yMid = (yTop + yBot) / 2;
    return <path key={k2} d={`M 250 ${yTop} Q 275 ${yMid} 250 ${yBot} Q 225 ${yMid + 2} 250 ${yBot}`} fill="none" stroke="#94a3b8" strokeWidth="2.5" />;
  });

  const scene = (
    <svg viewBox="0 0 500 500" className="w-full h-full" data-testid="practice-canvas">
      {/* Support */}
      <rect x="150" y="50" width="200" height="12" fill="#334155" />
      <line x1="250" y1="62" x2="250" y2={springTop} stroke="#94a3b8" strokeWidth="2" />
      {/* Spring */}
      {coils}
      {/* Ruler */}
      <line x1="360" y1={springTop} x2="360" y2={springTop + 260} stroke="#94a3b8" strokeWidth="1.5" />
      {[...Array(27)].map((_, k2) => (
        <g key={k2}>
          <line x1="355" y1={springTop + k2 * 10} x2="360" y2={springTop + k2 * 10} stroke="#64748b" strokeWidth="1" />
          {k2 % 5 === 0 && <text x="350" y={springTop + k2 * 10 + 3} textAnchor="end" fill="#64748b" fontSize="9" fontFamily="monospace">{k2}</text>}
        </g>
      ))}
      <line x1="360" y1={springTop + relaxedLen} x2="380" y2={springTop + relaxedLen} stroke="#22d3ee" strokeWidth="1.5" strokeDasharray="4 3" />
      <text x="390" y={springTop + relaxedLen + 4} fill="#22d3ee" fontSize="10" fontFamily="monospace">natural</text>
      <line x1="360" y1={springTop + stretched} x2="380" y2={springTop + stretched} stroke="#fde047" strokeWidth="2" />
      <text x="390" y={springTop + stretched + 4} fill="#fde047" fontSize="10" fontFamily="monospace">now</text>
      {/* Mass */}
      <g transform={`translate(220, ${springTop + stretched})`}>
        <rect x="0" y="0" width="60" height="46" fill="#eab308" stroke="#a16207" strokeWidth="2" rx="4" />
        <text x="30" y="22" fill="#0f172a" fontSize="11" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{`${m} g`}</text>
        <text x="30" y="38" fill="#0f172a" fontSize="9" textAnchor="middle" fontFamily="monospace">{`${(m / 1000 * 9.8).toFixed(2)} N`}</text>
      </g>
      <text x="70" y="40" fill="#22d3ee" fontSize="14" fontFamily="monospace">{`x = ${x.toFixed(2)} cm`}</text>
      <text x="70" y="58" fill="#94a3b8" fontSize="10" fontFamily="monospace">k = mg/x</text>
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4">
        <div className="text-xs text-slate-500 mb-2 font-mono">Mass hanging on spring</div>
        <Slider min={20} max={300} step={5} value={[m]} onValueChange={(v) => setM(v[0])} data-testid="spring-mass" />
        <div className="mt-3 flex justify-between text-xs text-slate-500 font-mono"><span>20 g</span><span className="text-cyan-300">m = {m} g</span><span>300 g</span></div>
        <Button size="sm" className="mt-3 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={check} data-testid="spring-check">Record extension</Button>
      </div>
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};

// ============================================================================
// ENZYME — catalase activity vs temperature
// ============================================================================
export const EnzymePractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [T, setT] = useState(25); // °C
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const [readings, setReadings] = useState({});
  const step = steps[stepIdx];

  // Peak at T=37, denatures beyond 60
  const activity = useMemo(() => {
    if (T > 65) return Math.max(0, 14 - (T - 65) * 0.5);
    const dev = (T - 37) / 15;
    return Math.max(0.5, 14 * Math.exp(-dev * dev));
  }, [T]);
  const [phase, setPhase] = useState(0);
  useEffect(() => { const t = setInterval(() => setPhase((p) => (p + 1) % 40), 100); return () => clearInterval(t); }, []);

  const check = () => {
    const id = step.id;
    const T_ok = (t) => Math.abs(T - t) <= 4;
    if (id === "p1" && T_ok(10)) { setStatus((s) => ({ ...s, p1: "ok" })); setReadings((r) => ({ ...r, T10: activity })); setFb({ type: "ok", msg: `${step.correct_hint} Bubble column ≈ ${activity.toFixed(1)} cm.` }); }
    else if (id === "p1") setFb({ type: "warn", msg: step.mistakes?.[0]?.hint || "Cool it to ~10 °C." });
    else if (id === "p2" && T_ok(37)) { setStatus((s) => ({ ...s, p2: "ok" })); setReadings((r) => ({ ...r, T37: activity })); setFb({ type: "ok", msg: `${step.correct_hint} Peak column ${activity.toFixed(1)} cm.` }); }
    else if (id === "p2") setFb({ type: "warn", msg: "Aim near 37 °C." });
    else if (id === "p3" && T_ok(90)) { setStatus((s) => ({ ...s, p3: "ok" })); setReadings((r) => ({ ...r, T90: activity })); setFb({ type: "ok", msg: `${step.correct_hint} Activity dropped to ${activity.toFixed(1)} cm.` }); }
    else if (id === "p3") setFb({ type: "warn", msg: "Push to ~90 °C." });
    else if (id === "p4") { setStatus((s) => ({ ...s, p4: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); }
  };
  const reset = () => { setT(25); setStatus({}); setFb(null); setReadings({}); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  // Water bath colour
  const bathColour = T < 20 ? "#60a5fa" : T < 45 ? "#22c55e" : T < 70 ? "#f97316" : "#ef4444";
  const bubbleHeight = activity * 20; // px
  const scene = (
    <svg viewBox="0 0 500 500" className="w-full h-full" data-testid="practice-canvas">
      {/* Water bath */}
      <rect x="60" y="200" width="380" height="230" fill={bathColour} opacity="0.25" stroke={bathColour} strokeWidth="2.5" />
      <text x="250" y="220" fill={bathColour} fontSize="12" textAnchor="middle" fontFamily="monospace">{`water bath · ${T} °C`}</text>
      {/* Test tube */}
      <path d="M 200 250 L 200 420 Q 200 435 220 435 L 260 435 Q 280 435 280 420 L 280 250 Z" fill="rgba(15,23,42,0.9)" stroke="#94a3b8" strokeWidth="2" />
      {/* H₂O₂ liquid */}
      <rect x="203" y="360" width="74" height="72" fill="#dbeafe" opacity="0.6" />
      {/* Bubble column */}
      <rect x="220" y={430 - bubbleHeight} width="40" height={bubbleHeight} fill="rgba(165,243,252,0.85)" />
      {[...Array(Math.round(activity))].map((_, k) => (
        <circle key={k} cx={225 + ((phase + k * 4) % 30)} cy={430 - k * 15 - (phase % 8)} r="2.5" fill="#a5f3fc" opacity="0.9" />
      ))}
      {/* Ruler */}
      <line x1="300" y1="230" x2="300" y2="430" stroke="#94a3b8" strokeWidth="1.5" />
      {[...Array(11)].map((_, k) => (
        <g key={k}>
          <line x1="295" y1={430 - k * 20} x2="305" y2={430 - k * 20} stroke="#64748b" strokeWidth="1" />
          <text x="310" y={430 - k * 20 + 3} fill="#64748b" fontSize="9" fontFamily="monospace">{k}</text>
        </g>
      ))}
      {/* Result gauge */}
      <g transform="translate(30, 40)">
        <rect x="0" y="0" width="180" height="60" fill="#0f172a" stroke="#22c55e" strokeWidth="2" rx="6" />
        <text x="90" y="22" fill="#22d3ee" fontSize="10" textAnchor="middle" fontFamily="monospace">Bubble column (cm)</text>
        <text x="90" y="48" fill="#fde047" fontSize="22" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{activity.toFixed(1)}</text>
      </g>
      {readings.T10 !== undefined && <text x="30" y="470" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`10°→${readings.T10.toFixed(1)} cm`}</text>}
      {readings.T37 !== undefined && <text x="180" y="470" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`37°→${readings.T37.toFixed(1)} cm`}</text>}
      {readings.T90 !== undefined && <text x="330" y="470" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`90°→${readings.T90.toFixed(1)} cm`}</text>}
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4">
        <div className="text-xs text-slate-500 mb-2 font-mono">Water bath temperature</div>
        <Slider min={0} max={100} step={1} value={[T]} onValueChange={(v) => setT(v[0])} data-testid="enzyme-temp" />
        <div className="mt-3 flex justify-between text-xs text-slate-500 font-mono"><span>0 °C</span><span className="text-cyan-300">T = {T} °C</span><span>100 °C</span></div>
        <Button size="sm" className="mt-3 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={check} data-testid="enzyme-check">Record this trial</Button>
      </div>
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};

// ============================================================================
// STOMATA — count stomata by tapping in a microscope field
// ============================================================================
const FIELDS_RAW = [
  // seeded positions of stomata for each field
  [[30, 40], [55, 25], [80, 55], [22, 70], [66, 78], [45, 50], [88, 82], [12, 32], [70, 15], [32, 88], [58, 62], [93, 45]],           // Field 1: 12
  [[25, 30], [50, 20], [75, 42], [15, 62], [60, 72], [40, 45], [82, 68], [22, 22], [65, 12], [30, 82], [55, 55], [88, 30], [70, 90], [12, 92], [45, 15], [95, 78], [8, 45], [80, 12]], // Field 2: 18
  [[35, 42], [60, 25], [85, 60], [22, 68], [72, 78], [45, 50], [90, 82], [12, 32], [70, 15], [32, 88], [55, 62], [95, 45], [18, 15], [80, 30], [50, 85]],   // Field 3: 15
];
// FOV clip is a circle at (250,260) r=200; drop any coord whose ellipse would land
// outside so that every rendered stoma is tappable.
const FOV_CX = 250, FOV_CY = 260, FOV_MARGIN = 15;
const inField = ([px, py]) => {
  const x = 55 + px * 3.9;
  const y = 65 + py * 3.9;
  return Math.hypot(x - FOV_CX, y - FOV_CY) <= 200 - FOV_MARGIN;
};
const FIELDS = FIELDS_RAW.map((f) => f.filter(inField));
export const StomataPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [fieldIdx, setFieldIdx] = useState(0);
  const [counted, setCounted] = useState(new Set()); // indices in current field
  const [totals, setTotals] = useState({}); // { 0: 12, 1: 18, 2: 15 }
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const step = steps[stepIdx];
  const target = FIELDS[fieldIdx].length;

  const tap = (i) => {
    if (counted.has(i)) return;
    const next = new Set(counted); next.add(i);
    setCounted(next);
    if (next.size === target) {
      const id = step.id;
      const idx_expected = ["p1", "p2", "p3"].indexOf(id);
      if (idx_expected === fieldIdx) {
        setStatus((s) => ({ ...s, [id]: "ok" }));
        setTotals((t) => ({ ...t, [fieldIdx]: target }));
        setFb({ type: "ok", msg: `${step.correct_hint} Counted ${target}.` });
      }
    }
  };
  const nextField = () => {
    if (fieldIdx < 2) { setFieldIdx(fieldIdx + 1); setCounted(new Set()); setFb(null); }
  };
  const verify = () => { setStatus((s) => ({ ...s, p4: "ok" })); setFb({ type: "ok", msg: step.correct_hint + ` Mean = ${((totals[0] + totals[1] + totals[2]) / 3).toFixed(1)} per field.` }); };
  const reset = () => { setFieldIdx(0); setCounted(new Set()); setStatus({}); setTotals({}); setFb(null); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => {
    if (stepIdx < steps.length - 1) {
      const nextId = steps[stepIdx + 1].id;
      if (["p2", "p3"].includes(nextId)) nextField();
      setStepIdx(stepIdx + 1); setFb(null);
    } else {
      onComplete?.();
    }
  };

  const scene = (
    <svg viewBox="0 0 500 500" className="w-full h-full" data-testid="practice-canvas">
      {/* Microscope field circle */}
      <defs>
        <clipPath id="fov"><circle cx="250" cy="260" r="200" /></clipPath>
      </defs>
      <circle cx="250" cy="260" r="200" fill="#dcfce7" stroke="#166534" strokeWidth="4" />
      <g clipPath="url(#fov)">
        {/* Cellular texture */}
        {[...Array(30)].map((_, k) => (
          <rect key={k} x={40 + (k % 6) * 70} y={60 + Math.floor(k / 6) * 70} width="60" height="60" fill="none" stroke="#86efac" strokeWidth="1" opacity="0.5" />
        ))}
        {/* Stomata */}
        {FIELDS[fieldIdx].map(([px, py], i) => {
          const x = 55 + px * 3.9;
          const y = 65 + py * 3.9;
          const isCounted = counted.has(i);
          return (
            <g key={i} onClick={() => tap(i)} style={{ cursor: "pointer" }} data-testid={`stomata-${fieldIdx}-${i}`}>
              <ellipse cx={x} cy={y} rx="10" ry="5" fill={isCounted ? "#22c55e" : "#166534"} stroke={isCounted ? "#166534" : "#065f46"} strokeWidth="1.5" opacity="0.95" />
              <ellipse cx={x} cy={y} rx="3" ry="4" fill="#0f172a" />
              {isCounted && <text x={x} y={y - 12} fill="#166534" fontSize="10" textAnchor="middle" fontFamily="monospace" fontWeight="bold">✓</text>}
            </g>
          );
        })}
      </g>
      {/* Counter */}
      <g transform="translate(30, 30)">
        <rect x="0" y="0" width="200" height="60" fill="#0f172a" stroke="#22c55e" strokeWidth="2" rx="6" />
        <text x="100" y="22" fill="#22d3ee" fontSize="10" textAnchor="middle" fontFamily="monospace">{`Field ${fieldIdx + 1} · Counted / Total`}</text>
        <text x="100" y="48" fill="#fde047" fontSize="20" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{`${counted.size} / ${target}`}</text>
      </g>
      {totals[0] !== undefined && <text x="30" y="480" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`F1:${totals[0]}`}</text>}
      {totals[1] !== undefined && <text x="90" y="480" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`F2:${totals[1]}`}</text>}
      {totals[2] !== undefined && <text x="150" y="480" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`F3:${totals[2]}`}</text>}
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      {step?.id === "p4" && (
        <Button className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={verify} data-testid="stomata-verify">Compute mean density</Button>
      )}
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};
